import { useRef } from 'react';

import { useDateFieldState } from '@react-stately/datepicker';
import type {
  DateFieldState,
  DateSegment as DateSegmentType,
} from '@react-stately/datepicker';
import { useDateField, useDateSegment } from '@react-aria/datepicker';
import type { AriaDatePickerProps, DateValue } from '@react-aria/datepicker';

import { createCalendar } from '@internationalized/date';

export const DateInput = (
  props: AriaDatePickerProps<DateValue> & {
    lang: 'en-GB' | 'en-US' | 'en-CA';
    className?: string;
  }
) => {
  const { className, lang, 'aria-label': ariaLabel } = props;

  const state = useDateFieldState({
    ...props,
    locale: lang,
    createCalendar,
  });

  const ref = useRef(null);

  const { fieldProps } = useDateField(props, state, ref);

  return (
    <div aria-label={ariaLabel} className={className}>
      <div
        {...fieldProps}
        ref={ref}
        className="grid items-center justify-start"
      >
        <div>
          {state.segments.map((segment, i) => (
            <DateSegment key={i} segment={segment} state={state} />
          ))}
        </div>
      </div>
    </div>
  );
};

function DateSegment({
  segment,
  state,
}: {
  segment: DateSegmentType;
  state: DateFieldState;
}) {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  let { text } = segment;
  const { type } = segment;

  // react-aria uses locale to format dates and includes separators within its segments
  // Since Intl.DateTimeFormat "en-CA" will return 'YYYY-MM-DD', we need to replace '-' with '/'
  // The end goal is to satisfy the date format 'YYYY/MM/DD' as per product requirements
  if (type === 'literal' && text === '-') {
    text = '/';
  }

  return (
    <div
      {...segmentProps}
      ref={ref}
      className={`segment ${
        segment.isPlaceholder ? 'placeholder' : ''
      } focus:rounded-x0_5  focus:bg-brand-01 focus:text-solid ${
        type === 'literal' ? 'p-x0' : 'p-x0_25'
      } caret-transparent`}
      style={{ display: 'inline-block' }}
    >
      {text}
    </div>
  );
}
